import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react'
import { uploadTechAvatar } from '../../api'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import PrimaryCheckbox from '../../../../../global/components/checkbox/Checkbox'
import techAvatarIcon from '../../../../../assets/images/tech-avatar-icon.svg'
import styles from './styles.module.css'
import { fileToDataUri, parseTimeStringToMinutes } from '../utils'
import {
  TECHNICIAN_PROFILE_IMAGE_INPUT_MAX_IMAGE_HEIGHT,
  TECHNICIAN_PROFILE_IMAGE_INPUT_MAX_IMAGE_WIDTH,
  TECHNICIAN_PROFILE_IMAGE_INPUT_MAX_SIZE_MB,
  TECHNICIAN_PROFILE_IMAGE_INPUT_TYPES_ARRAY,
} from '../../../../../global/constants/technician'
import { ITechnicianPartial } from '../../../../../../../app/entities/Technician'
import { fetchData } from '../../../../../global/utils/fetch'
import { ILocation } from '../../../../../../../app/entities/Location'

interface ITechnicianInputColumnProps {
  technicianData: ITechnicianPartial
  setTechnicianData: Function
  isDefaultWorkDay: boolean
  setIsDefaultWorkDay: Dispatch<SetStateAction<boolean>>
  isDefaultLunch: boolean
  setIsDefaultLunch: Dispatch<SetStateAction<boolean>>
  location: ILocation | null
}

export default function TechInputColumn({
  technicianData,
  setTechnicianData,
  isDefaultLunch,
  setIsDefaultLunch,
  isDefaultWorkDay,
  setIsDefaultWorkDay,
  location,
}: ITechnicianInputColumnProps) {
  const {
    name,
    email,
    bio,
    profilePicture,
    workDayFrom,
    workDayTo,
    lunchDuration,
  } = technicianData

  const locationDefaultWorkDayFrom = location?.workDayFrom
  const locationDefaultWorkDayTo = location?.workDayTo
  const locationDefaultLunchDuration = location?.lunchDuration

  const [isLoading, setIsLoading] = useState(false)
  const [workDayFromValue, setWorkDayFromValue] = useState(
    isDefaultWorkDay ? locationDefaultWorkDayFrom : workDayFrom,
  )
  const [workDayToValue, setWorkDayToValue] = useState(
    isDefaultWorkDay ? locationDefaultWorkDayTo : workDayTo,
  )
  const [lunchDurationValue, setLunchDurationValue] = useState(
    isDefaultLunch ? locationDefaultLunchDuration : lunchDuration,
  )

  useEffect(() => {
    setLunchDurationValue(
      isDefaultLunch ? locationDefaultLunchDuration : lunchDuration,
    )
    setWorkDayToValue(isDefaultWorkDay ? locationDefaultWorkDayTo : workDayTo)
    setWorkDayFromValue(
      isDefaultWorkDay ? locationDefaultWorkDayFrom : workDayFrom,
    )
  }, [isDefaultWorkDay, isDefaultLunch])

  function updateTechForm(e: ChangeEvent<HTMLInputElement>) {
    setTechnicianData({
      ...technicianData,
      [e.target.name]: e.target.value,
    })
  }

  function handleWorkDayFromChange(e: ChangeEvent<HTMLInputElement>) {
    if (!isDefaultWorkDay) {
      const value = e.target.value
      setWorkDayFromValue(value)
      setTechnicianData({
        ...technicianData,
        workDayFrom: value,
      })
    }
  }

  function handleWorkDayToChange(e: ChangeEvent<HTMLInputElement>) {
    if (!isDefaultWorkDay) {
      const value = e.target.value
      setWorkDayToValue(value)
      setTechnicianData({
        ...technicianData,
        workDayTo: value,
      })
    }
  }

  function handleLunchDurationChange(e: ChangeEvent<HTMLInputElement>) {
    if (!isDefaultLunch) {
      const value = e.target.value
      setLunchDurationValue(value === '' ? undefined : Number(value))
      setTechnicianData({
        ...technicianData,
        lunchDuration: value === '' ? undefined : Number(value),
      })
    }
  }

  async function handleImageFileInput(e: any) {
    const formData = new FormData()
    const file = e.target.files[0]
    const fileType = file.type.split('/')[1]
    const image = new Image()

    if (!TECHNICIAN_PROFILE_IMAGE_INPUT_TYPES_ARRAY.includes(fileType)) {
      alert('You must upload a jpeg or png!')
      return
    }

    await fileToDataUri(file).then((dataUri) => {
      image.src = dataUri as string
    })
    if (
      image.height > TECHNICIAN_PROFILE_IMAGE_INPUT_MAX_IMAGE_HEIGHT &&
      image.width > TECHNICIAN_PROFILE_IMAGE_INPUT_MAX_IMAGE_WIDTH
    ) {
      alert(
        `Your image's height and width must be under ${TECHNICIAN_PROFILE_IMAGE_INPUT_MAX_IMAGE_HEIGHT}px x ${TECHNICIAN_PROFILE_IMAGE_INPUT_MAX_IMAGE_WIDTH}px!`,
      )
      return
    }
    if (file.size > TECHNICIAN_PROFILE_IMAGE_INPUT_MAX_SIZE_MB) {
      alert(`Your image must be under 3MB!`)
      return
    }

    formData.append('file', file)

    try {
      setIsLoading(true)
      await fetchData(uploadTechAvatar(technicianData.objectId ?? '', formData))
    } catch (error) {
      alert(error)
    } finally {
      setIsLoading(false)
    }
  }

  const isValidTechnicianWorkTime =
    parseTimeStringToMinutes(workDayTo) > parseTimeStringToMinutes(workDayFrom)

  return (
    <div className={styles.mainContainer}>
      <div className={styles.avatarContainer}>
        <div className={styles.imageContainer}>
          <img
            src={profilePicture ? profilePicture : techAvatarIcon}
            alt='tech avatar'
          />
        </div>
        <input
          style={{ display: 'none' }}
          id='imageUpload'
          className={styles.uploadedFile}
          onChange={handleImageFileInput}
          type='file'
        />
        <label
          htmlFor='imageUpload'
          className={styles.uploadButton}
        >
          Upload Profile Image
        </label>
        {isLoading && <CircularProgress size={20} />}
      </div>
      <div className={styles.inputsContainer}>
        <TextField
          label='Name'
          name='name'
          value={name || ''}
          onChange={updateTechForm}
          fullWidth
        />
        <TextField
          label='Email'
          name='email'
          value={email || ''}
          onChange={updateTechForm}
          fullWidth
        />
        <TextField
          multiline
          rows={5}
          label='Bio'
          fullWidth
          name='bio'
          value={bio || ''}
          onChange={updateTechForm}
        />
        <div className={styles.hours}>
          <div className={styles.hoursHeading}>Working Hours</div>
          <div className={styles.useLocationDefault}>
            <PrimaryCheckbox
              name='useDefaultWorkDay'
              checked={isDefaultWorkDay}
              onChange={(e) => setIsDefaultWorkDay(e.target.checked)}
              label='Use Location Default'
            />
          </div>
          <div className={styles.hoursInputs}>
            <TextField
              InputLabelProps={{ shrink: true }}
              type='time'
              label='From'
              name='workDayFrom'
              value={workDayFromValue ?? ''}
              onChange={isDefaultWorkDay ? undefined : handleWorkDayFromChange}
              disabled={isDefaultWorkDay}
            />
            <TextField
              InputLabelProps={{ shrink: true }}
              type='time'
              label='To'
              name='workDayTo'
              value={workDayToValue ?? ''}
              onChange={isDefaultWorkDay ? undefined : handleWorkDayToChange}
              disabled={isDefaultWorkDay}
            />
          </div>
          {!location && isDefaultWorkDay && (
            <div className={styles.technicianModalError}>
              Location must be selected to use location default
            </div>
          )}
          {!isValidTechnicianWorkTime && !isDefaultWorkDay && (
            <div className={styles.technicianModalError}>
              End time must be after start time
            </div>
          )}
        </div>
        <div className={styles.hours}>
          <div className={styles.hoursHeading}>Lunch Duration</div>
          <div className={styles.useLocationDefault}>
            <PrimaryCheckbox
              name='useDefaultLunch'
              checked={isDefaultLunch}
              onChange={(e) => setIsDefaultLunch(e.target.checked)}
              label='Use Location Default'
            />
          </div>
          <div>
            <TextField
              InputLabelProps={{ shrink: true }}
              type='number'
              label='Lunch Duration'
              name='lunchDuration'
              value={lunchDurationValue ?? ''}
              onChange={isDefaultLunch ? undefined : handleLunchDurationChange}
              disabled={isDefaultLunch}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
